import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmWithPassword } from 'components/Confirm';
import Page, { PageContainer } from 'components/Page';
import { Button, Divider, Form, Input, Modal, Select, withModal, Table, Header, Grid } from 'components/semantic';
import moment from 'moment';
import { toast } from 'components/Toast';
import request, { CLIENT_CENTER_VERSION } from 'utils/request';
import { withTracker } from 'utils/tracker';
import { useUser } from 'App/User';
import { AccountMoney, useAccountMoney } from 'App/AccountMoney';
import { formatMoney, formatTime, TimeFormat } from 'utils';
import { isUS } from 'env';
import { useAPI, useLocalState } from 'utils/use-api';
import ExternalLink from 'components/ExternalLink';
import { FileUploader, LCFile } from 'App/Application/Storage/File/FileUploader';
import { consoleApp } from 'utils';
import ImageThumbGrid, { ImageFile } from 'components/ImageThumbGrid';

type PaymentChannel = 'bank' | 'stripe' | 'alipay';

interface BankFormData extends Record<string, unknown> {
  accountNumber?: string;
  accountName?: string;
  swiftCode?: string;
  bankBranch?: string;
  bankAddress?: string;
}

interface StripeFormData extends Record<string, unknown> {
  email?: string;
  lastPaymentID?: string;
}

interface AlipayFormData extends Record<string, unknown> {
  alipayAccount?: string;
}

interface PaymentData {
  alipay?: AlipayFormData;
  stripe?: StripeFormData;
  bank?: BankFormData;
  supportDocuments?: string[];
}

type RefundStatus = 'terminated' | 'pending' | 'offsetInvoice' | 'invoiceConfirmed' | 'paying' | 'refunded';

interface RefundData {
  payment: PaymentData;
  amountCent: number;
  terminatedAt?: Date;
  refundedAt?: Date;
  updatedAt?: Date;
  status: RefundStatus;
  invoice?: {
    amountCent: number;
    company: string;
  };
  remark?: string;
}

const statusIcon = (status: RefundStatus | undefined) => {
  switch (status) {
    case 'pending':
      return '⚠️';
    case 'offsetInvoice':
      return '📝';
    case 'paying':
      return '⏳';
    case 'refunded':
      return '✅';
    default:
      return '🚧';
  }
}

const useRefundData = () => {
  return useLocalState(
    useAPI<RefundData>(`/client-center/${CLIENT_CENTER_VERSION}/clients/self/sunset/refund`, {
      method: 'GET',
    })
  );
}

const StripeForm = ({ data, onChange }: { data: StripeFormData, onChange: (data: StripeFormData) => void }) => {
  const { t } = useTranslation();

  const handleChange = (field: string, value: string) => {
    onChange({ [field]: value, });
  }

  return (
    <>
      <Form.Field required>
        <label htmlFor="stripeEmail">{t('account.sunset.refund.stripe.email')}</label>
        <Input id="stripeEmail" name="email"
          value={data.email ?? ''}
          onChange={(e, data) => handleChange('email', data.value)} />
        <p className="help-block">{t('account.sunset.refund.stripe.email.hint')}</p>
      </Form.Field>
      <Form.Field required>
        <label htmlFor="stripePaymentID">{t('account.sunset.refund.stripe.lastPaymentID')}</label>
        <Input id="stripePaymentID" name="lastPaymentID"
          value={data.lastPaymentID ?? ''}
          onChange={(e, data) => handleChange('lastPaymentID', data.value)} />
        <p className="help-block">
          <Trans
            i18nKey="account.sunset.refund.stripe.lastPaymentID.hint"
            components={{ code: <code /> }}
          />
        </p>
      </Form.Field>
    </>
  )
}

const MoneyAccountTable = ({ moneyAccount }: { moneyAccount: AccountMoney | undefined }) => {
  const { t } = useTranslation();
  if (!moneyAccount) {
    return null;
  }
  return (
    <Table collapsing structured striped>
      <Table.Body>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.balance.total')}</Header></Table.Cell>
          <Table.Cell colSpan={2}>{formatMoney(Number(moneyAccount?.amount ?? 0))}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.balance.gift')}</Header></Table.Cell>
          <Table.Cell>{formatMoney(Number(moneyAccount?.gift ?? 0))}</Table.Cell>
          <Table.Cell className='help-block'>
            {t('account.sunset.refund.balance.gift.hint')}
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.balance.amount')}</Header></Table.Cell>
          <Table.Cell>{formatMoney(Number(moneyAccount?.balance ?? 0))}</Table.Cell>
          <Table.Cell className='help-block'>
            {t('account.sunset.refund.balance.amount.hint')}
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  )
}

const RefundDetailTable = ({ refundData }: { refundData: RefundData | undefined }) => {
  const { t } = useTranslation();
  const amount = (refundData?.amountCent ?? 0) / 100;
  const invoiceAmount = (refundData?.invoice?.amountCent ?? 0) / 100;
  return (
    <Table collapsing structured striped>
      <Table.Body>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.amount')}</Header></Table.Cell>
          <Table.Cell>{formatMoney(Number(amount.toFixed(2)))}</Table.Cell>
        </Table.Row>
        {refundData?.payment?.stripe && (
          <>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.channel.stripe')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.stripe.email}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.stripe.lastPaymentID')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.stripe.lastPaymentID}</Table.Cell>
            </Table.Row>
          </>
        )}
        {refundData?.payment?.bank?.swiftCode && (
          <Table.Row>
            <Table.Cell><Header as='h5'>{t('account.sunset.refund.swiftCode')}</Header></Table.Cell>
            <Table.Cell>{refundData?.payment.bank.swiftCode}</Table.Cell>
          </Table.Row>
        )}
        {refundData?.payment?.bank && (
          <>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.bankBranch')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.bank.bankBranch}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.bankAddress')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.bank.bankAddress}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.accountNumber')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.bank.accountNumber}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell><Header as='h5'>{t('account.sunset.refund.accountName')}</Header></Table.Cell>
              <Table.Cell>{refundData?.payment.bank.accountName}</Table.Cell>
            </Table.Row>
          </>
        )}
        {refundData?.invoice && (
          <>
          <Table.Row>
            <Table.Cell><Header as='h5'>{t('account.sunset.refund.invoice.company')}</Header></Table.Cell>
            <Table.Cell>{refundData.invoice?.company}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell><Header as='h5'>{t('account.sunset.refund.invoice.amount')}</Header></Table.Cell>
            <Table.Cell>{formatMoney(Number(invoiceAmount.toFixed(2)))}</Table.Cell>
          </Table.Row>
          </>
        )}
        {refundData?.payment?.supportDocuments && refundData.payment.supportDocuments.length > 0 && (
          <Table.Row>
            <Table.Cell><Header as='h5'>{t('account.sunset.refund.supportDocuments')}</Header></Table.Cell>
            <Table.Cell>
              <ImageThumbGrid
                images={refundData.payment.supportDocuments.map((url) => ({ url }))}
              />
            </Table.Cell>
          </Table.Row>
        )}
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.remark')}</Header></Table.Cell>
          <Table.Cell>{refundData?.remark}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('account.sunset.refund.status')}</Header></Table.Cell>
          <Table.Cell>{`${statusIcon(refundData?.status)} ${t(`account.sunset.refund.status.${refundData?.status}`)}`}
          </Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell><Header as='h5'>{t('label.updatedAt')}</Header></Table.Cell>
          <Table.Cell>{formatTime(refundData?.updatedAt ?? new Date(), TimeFormat.DISPLAY_DATETIME)}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  )
}

const BankForm = ({ data, onChange }: { data: BankFormData, onChange: (data: BankFormData) => void }) => {
  const { t } = useTranslation();

  const handleChange = (field: string, value: string) => {
    onChange({ [field]: value, });
  }

  return (
    <>
      {isUS && (
        <Form.Field>
          <label htmlFor="swiftCode">{t('account.sunset.refund.swiftCode')}</label>
          <Input id="swiftCode" name="swiftCode"
            value={data.swiftCode ?? ''}
            onChange={(e, data) => handleChange('swiftCode', data.value)} />
        </Form.Field>
      )}
      <Form.Field required>
        <label htmlFor="bankBranch">{t('account.sunset.refund.bankBranch')}</label>
        <Input id="bankBranch" name="bankBranch"
          value={data.bankBranch ?? ''}
          onChange={(e, data) => handleChange('bankBranch', data.value)} />
        <p className="help-block">{t('account.sunset.refund.bankBranch.hint')}</p>
      </Form.Field>
      <Form.Field required>
        <label htmlFor="bankAddress">{t('account.sunset.refund.bankAddress')}</label>
        <Input id="bankAddress" name="bankAddress"
          value={data.bankAddress ?? ''}
          onChange={(e, data) => handleChange('bankAddress', data.value)} />
        <p className="help-block">{t('account.sunset.refund.bankAddress.hint')}</p>
      </Form.Field>
      <Form.Field required>
        <label htmlFor="accountNumber">{t('account.sunset.refund.accountNumber')}</label>
        <Input id="accountNumber" name="accountNumber"
          value={data.accountNumber ?? ''}
          onChange={(e, data) => handleChange('accountNumber', data.value)} />
        <p className="help-block">{t('account.sunset.refund.accountNumber.hint')}</p>
      </Form.Field>
      <Form.Field required>
        <label htmlFor="accountName">{t('account.sunset.refund.accountName')}</label>
        <Input id="accountName" name="accountName"
          value={data.accountName ?? ''}
          onChange={(e, data) => handleChange('accountName', data.value)} />
        <p className="help-block">{t('account.sunset.refund.accountName.hint')}</p>
      </Form.Field>
    </>
  )
}

const RefundModal = withModal<{
  onSuccessed: () => void,
  refundData: RefundData | undefined,
  moneyAccount: AccountMoney | undefined
}>()(({ moneyAccount, close, onSuccessed, refundData }) => {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<PaymentChannel>(refundData?.payment?.stripe ? 'stripe' : 'bank');
  const [paymentData, setPaymentData] = useState<PaymentData>(refundData?.payment ?? {
    alipay: {},
    stripe: {},
    bank: {},
  });
  const [loading, setLoading] = useState(false);
  const [supportDocuments, setSupportDocuments] = useState<ImageFile[]>(
    refundData?.payment?.supportDocuments?.map((url) => ({ url })) ?? []
  );

  const submit = useCallback(async () => {
    setLoading(true);
    const amountCent = Math.round(Number(moneyAccount?.balance ?? 0) * 100);
    request(`/client-center/${CLIENT_CENTER_VERSION}/clients/self/sunset/refund`, {
      method: 'PUT',
      body: {
        payment: {
          ...paymentData,
          supportDocuments: supportDocuments.map((doc) => doc.url)
        },
        amountCent
      },
    }).then(() => {
      toast.success(t('action.save.successfully'));
      onSuccessed();
      close();
    }).catch((error) => {
      toast.error(t('action.save.failed'), error);
    }).finally(() => {
      setLoading(false);
    });
  }, [moneyAccount, paymentData, supportDocuments, onSuccessed, t, close]);

  const handlePaymentDataChange = (channel: keyof PaymentData, data: Record<string, unknown>) => {
    setPaymentData((prev: PaymentData) => ({ 
      [channel]: { ...prev[channel], ...data } 
    }));
  }

  const channelOptions = isUS ? [
    { text: t('account.sunset.refund.channel.bank'), value: 'bank' },
    { text: t('account.sunset.refund.channel.stripe'), value: 'stripe' },
  ] : [
    { text: t('account.sunset.refund.channel.bank'), value: 'bank' },
  ];

  return (
    <>
      <Modal.Header>
        {t('account.sunset.refund.apply')}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Field inline>
            <label htmlFor="amount">{t('account.sunset.refund.amount')}</label>
            <Input id="refundAmount" name="amount" disabled value={formatMoney(Number(moneyAccount?.balance ?? 0))} />
          </Form.Field>
          <Divider horizontal >{t('account.sunset.refund.payment')}</Divider>
          <p className="help-block">
            <Trans
              i18nKey="account.sunset.refund.guide"
              components={{
                Link: <ExternalLink href={`/docs/sdk/announcements/sunset-refund/`} />,
              }}
            />
          </p>
          <Form.Field>
            <label htmlFor="channel">{t('account.sunset.refund.channel')}</label>
            <Select id="channel" name="channel" options={channelOptions} value={channel}
              onChange={(e, data) => setChannel(data.value as PaymentChannel)} />
          </Form.Field>
          {channel === 'bank' &&
            <BankForm data={paymentData.bank ?? {}} onChange={(data) => handlePaymentDataChange('bank', data)} />}
          {channel === 'stripe' &&
            <StripeForm data={paymentData.stripe ?? {}} onChange={(data) => handlePaymentDataChange('stripe', data)} />}
          <Divider horizontal>{t('account.sunset.refund.supportDocuments')}</Divider>
          <Form.Field>
            <label>{t('account.sunset.refund.supportDocuments')}</label>
            <FileUploader
              app={consoleApp}
              id="supportDocuments"
              name="supportDocuments"
              onUploaded={(_, files: LCFile[]) => {
                setSupportDocuments((prev) => [
                  ...prev,
                  ...files.map((file) => ({ url: file.url, name: file.name })),
                ]);
              }}
            />
            <ImageThumbGrid
              images={supportDocuments}
              onDelete={(url) => {
                setSupportDocuments((prev) => prev.filter((doc) => doc.url !== url));
              }}
            />
            <p className="help-block">{t('account.sunset.refund.supportDocuments.hint')}</p>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={close} content={t('action.close')} />
        <Button type="submit" loading={loading} disabled={loading} primary content={t('account.sunset.refund.apply')} onClick={submit} />
      </Modal.Actions>
    </>
  );
});

export default withTracker(() => {
  const { t } = useTranslation();
  const [moneyAccount ] = useAccountMoney();
  const [refundData, { error, reload}] = useRefundData();
  const [user] = useUser();

  if (error && error.code !== 404) {
    toast.error(t('action.query.failed'), error.message);
  }
  const disableTerminate = useMemo(() => {
   return refundData && refundData?.terminatedAt !== undefined;
  }, [refundData]);

  const allowApply = useMemo(() => {
    if (refundData?.status === 'terminated' && refundData?.terminatedAt) {
      const terminatedAt = moment(refundData?.terminatedAt);
      return (moneyAccount?.balance || 0) > 0 && terminatedAt.isBefore(moment().subtract(3, 'days'))
    }
    return false;
  }, [refundData?.status, refundData?.terminatedAt, moneyAccount]);

  const allowEdit = useMemo(() => {
    return ['pending', 'offsetInvoice', 'invoiceConfirmed'].includes(refundData?.status || '');
  }, [refundData?.status]);

  const allowConfirmInvoice = useMemo(() => {
    return refundData?.status === 'offsetInvoice';
  }, [refundData?.status]);

  const displayProgress = useMemo(() => {
    return refundData?.payment && true;
  }, [refundData?.payment]);

  const confirmInvoice = () => {
    request(`/client-center/${CLIENT_CENTER_VERSION}/clients/self/sunset/refund`, {
      method: 'PUT',
      body: {
        status: 'invoiceConfirmed',
      },
    }).then(() => {
      toast.success(t('action.save.successfully'));
      reload();
    }).catch((error) => {
      toast.error(t('action.save.failed'), error);
    });
  }

  const terminateAccount = useCallback(
    (password: string, close: () => void) =>
      request(`/client-center/${CLIENT_CENTER_VERSION}/clients/self/sunset/terminate`, {
        method: 'POST',
        body: {
          password,
        },
      }).then(() => {
        toast.success(t('action.save.successfully'));
        close();
        reload();
      }).catch((error) => {
        toast.error(t('action.save.failed'), error);
      }),
    [t, reload]
  );

  if (!user) {
    return null;
  }
  return (
    <Page title={t('account.sunset.title')}>
      <PageContainer>
        <h3>{t('account.sunset.title')}</h3>
        <p><Trans components={{ strong: <strong /> }}>{t('account.sunset.intro')}</Trans></p>
        <ConfirmWithPassword
          trigger={<Button negative content={t('account.sunset.terminate')} disabled={disableTerminate} />}
          header={
            <>
              {t('account.sunset.terminate')} <code>{user.email}</code>
            </>
          }
          content={t('account.sunset.terminate.irreversible')}
          confirmButtonText={t('account.sunset.terminate.confirm')}
          onConfirm={terminateAccount}
        />
        <Divider section />
        <Grid columns={2}>
          <Grid.Row>
            <Grid.Column>
              <Header>{t('account.sunset.refund.balance')}</Header>
              <MoneyAccountTable moneyAccount={moneyAccount} />
              <p className='help-block'>{t('account.sunset.refund.hint')}
                <Trans
                  i18nKey="account.sunset.refund.guide"
                  components={{
                    Link: <ExternalLink href={`/docs/sdk/announcements/sunset-refund/`} />,
                  }}
                />
              </p>
              <RefundModal
                refundData={refundData}
                moneyAccount={moneyAccount}
                modalProps={{ trigger: <Button primary content={t('account.sunset.refund.apply')} disabled={!allowApply} /> }}
                onSuccessed={reload} />
            </Grid.Column>
            <Grid.Column>
              {displayProgress && (
                <>
                  <Header>{t('account.sunset.refund.detail')}</Header>
                  <RefundDetailTable refundData={refundData} />
                  {refundData?.invoice && (
                    <p className='help-block'>{t('account.sunset.refund.status.offsetInvoice.hint')}</p>
                  )}
                  <RefundModal
                    refundData={refundData}
                    moneyAccount={moneyAccount}
                    modalProps={{ trigger: <Button primary content={t('account.sunset.refund.edit')} disabled={!allowEdit} /> }}
                    onSuccessed={reload} />
                  {refundData?.invoice && (
                    <Button negative content={t('account.sunset.refund.confirmInvoice')} onClick={confirmInvoice} disabled={!allowConfirmInvoice} />
                  )}
                </>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </PageContainer>
    </Page>
  );
});
